import { type ForwardedRef, forwardRef, memo, type ReactElement } from 'react';
import type { GroupBase, SelectInstance } from 'react-select';
import ReactAsyncCreatableSelect, { type AsyncCreatableProps } from 'react-select/async-creatable';

import type { SelectOption } from '@didaskill/types/web';

import {
  type CommonSelectProps,
  LOADING_MESSAGE,
  NO_OPTIONS_MESSAGE,
  PLACEHOLDER_DEFAULT_VALUE,
  SELECT_SIZES,
} from './constants';
import { useReactSelectCustomization } from './utils/useReactSelectCustomizations';
import type { useReactSelectValue } from './utils/useReactSelectValueWrapper';

export type AsyncCreatableSelectProps<
  TOption extends SelectOption<any> = SelectOption<any>,
  TIsMulti extends boolean = false,
  TGroup extends GroupBase<TOption> = GroupBase<TOption>,
> = Omit<
  AsyncCreatableProps<TOption, TIsMulti, TGroup>,
  'isDisabled' | 'formatOptionLabel' | 'value' | 'onChange' | 'options'
> &
  CommonSelectProps & {
    onChange: Parameters<
      typeof useReactSelectValue<TOption, TGroup, TIsMulti, true>
    >[0]['onChange'];
    value: Parameters<typeof useReactSelectValue<TOption, TGroup, TIsMulti, true>>[0]['value'];
  };

const AsyncCreatableSelectInner = <
  TOption extends SelectOption<any>,
  TIsMulti extends boolean,
  TGroup extends GroupBase<TOption>,
>(
  {
    id = null,
    size = SELECT_SIZES.MEDIUM,
    disabled = false,
    hasError = false,
    hasWarning = false,
    noOptionsMessage = NO_OPTIONS_MESSAGE,
    placeholder = PLACEHOLDER_DEFAULT_VALUE,
    loadingMessage = LOADING_MESSAGE,
    ...props
  }: AsyncCreatableSelectProps<TOption, TIsMulti, TGroup>,
  ref: ForwardedRef<SelectInstance<TOption, TIsMulti, TGroup>>,
) => {
  const { onChange, onChangeMulti, ...customProps } = useReactSelectCustomization<
    TOption,
    TIsMulti,
    TGroup,
    true
  >({
    hasError,
    hasWarning,
    size,
    useOptionAsValue: true,
    ...props,
  });

  return (
    <ReactAsyncCreatableSelect<TOption, TIsMulti, TGroup>
      {...props}
      {...customProps}
      ref={ref}
      inputId={id}
      aria-labelledby={id}
      onChange={props.isMulti ? onChangeMulti : onChange}
      isDisabled={disabled}
      noOptionsMessage={noOptionsMessage}
      placeholder={placeholder}
      loadingMessage={loadingMessage}
    />
  );
};

export const AsyncCreatableSelectBase = forwardRef(AsyncCreatableSelectInner) as {
  <
    TOption extends SelectOption<any> = SelectOption<any>,
    TIsMulti extends boolean = false,
    TGroup extends GroupBase<TOption> = GroupBase<TOption>,
  >(
    props: AsyncCreatableSelectProps<TOption, TIsMulti, TGroup> & {
      ref?: ForwardedRef<SelectInstance<TOption, TIsMulti, TGroup>>;
    },
  ): ReactElement | null;

  displayName?: any;
};

AsyncCreatableSelectBase.displayName = 'AsyncCreatableSelect';

export const AsyncCreatableSelect = memo(
  AsyncCreatableSelectBase,
) as typeof AsyncCreatableSelectBase & {
  SIZES?: typeof SELECT_SIZES;
};

AsyncCreatableSelect.SIZES = SELECT_SIZES;
